import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { OptimizedRouteWrapper as RouteWrapper } from '@/components/common/OptimizedRouteWrapper';
import { AttendanceCheckInHandler } from '@/components/attendance/AttendanceCheckInHandler';
import { ROUTE_COMPONENTS } from '@/hooks/useRouteConfig';
import LandingPage from '@/pages/LandingPage';
import AuthPage from '@/pages/AuthPage';
import PasswordResetPage from '@/pages/PasswordResetPage';
import { PendingApprovalPage } from '@/pages/PendingApprovalPage';
import BackfillEventPage from '@/pages/BackfillEventPage';
import NotFound from '@/pages/NotFound';

export function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      {/* Home route */}
      <Route 
        path="/" 
        element={
          user ? (
            <RouteWrapper requiresAuth>
              <ROUTE_COMPONENTS.MegaAppHome />
            </RouteWrapper>
          ) : (
            <LandingPage />
          )
        } 
      />

      {/* Auth routes */}
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/login" element={<AuthPage />} />
      <Route path="/reset-password" element={<PasswordResetPage />} />
      <Route path="/pending-approval" element={<PendingApprovalPage />} />

      {/* Tracker routes */}
      <Route path="/disc" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.DISCTestPage /></RouteWrapper>} />

      {/* Tracker routes */}
      <Route path="/tracker/log" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.LogActivityPage /></RouteWrapper>} />
      <Route path="/tracker/feed" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.ActivityFeedPage /></RouteWrapper>} />
      <Route path="/tracker/pet" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.PetPage /></RouteWrapper>} />
      <Route path="/tracker/redeem" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.RedemptionPage /></RouteWrapper>} />
      <Route path="/tracker/reflections" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.WeeklyReflectionsPage /></RouteWrapper>} />
      <Route path="/tracker/club" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.ClubPage /></RouteWrapper>} />
      <Route path="/tracker/pledge-calculator" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.PledgeCalculatorPage /></RouteWrapper>} />
      <Route path="/tracker/activity-calculator" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.ActivityCalculator /></RouteWrapper>} />
      <Route path="/tracker/user-breakdown/:userId" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.UserActivityBreakdownPage /></RouteWrapper>} />
      
      {/* Tracker Admin routes */}
      <Route path="/tracker/admin/consultants" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.TrackerAdminPage /></RouteWrapper>} />
      <Route path="/tracker/admin/analytics" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.TrackerAdminPage /></RouteWrapper>} />
      <Route path="/tracker/admin/goals" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.TrackerAdminPage /></RouteWrapper>} />
      <Route path="/tracker/admin/rewards" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.TrackerAdminPage /></RouteWrapper>} />
      <Route path="/tracker/admin/user-approval" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.TrackerAdminPage /></RouteWrapper>} />
      <Route path="/tracker/admin/apps" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.TrackerAdminPage /></RouteWrapper>} />
      <Route path="/tracker/admin/settings" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.TrackerAdminPage /></RouteWrapper>} />
      <Route path="/tracker/admin" element={<Navigate to="/tracker/admin/consultants" replace />} />
      
      <Route path="/tracker/stats" element={<Navigate to="/tracker/redeem" replace />} />

      {/* Admin and management routes */}
      <Route path="/admin" element={<Navigate to="/tracker/admin" replace />} />
      <Route path="/feedback" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.Feedback /></RouteWrapper>} />
      <Route path="/income-calculator" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.InsuranceCalculatorPage /></RouteWrapper>} />
      <Route path="/coaching" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.CoachingSettings /></RouteWrapper>} />

      {/* Attendance routes */}
      <Route path="/attendance/:tab" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.AttendancePage /></RouteWrapper>} />
      <Route path="/attendance" element={<Navigate to="/attendance/calendar" replace />} />
      <Route path="/attendance-checkin" element={<AttendanceCheckInHandler />} />
      <Route path="/attendance/leave-requests" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.LeaveRequestsPage /></RouteWrapper>} />
      <Route path="/attendance/leave-request/new" element={<Navigate to="/attendance/leave-requests" replace />} />
      <Route path="/attendance-tracker" element={user ? <Navigate to="/attendance" /> : <RouteWrapper><ROUTE_COMPONENTS.AttendanceTrackerLandingPage /></RouteWrapper>} />

      {/* Admin attendance routes */}
      <Route path="/attendance/admin/analytics/by-event" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.AttendanceAdminPage /></RouteWrapper>} />
      <Route path="/attendance/admin/analytics/by-user" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.AttendanceAdminPage /></RouteWrapper>} />
      <Route path="/attendance/admin/analytics" element={<Navigate to="/attendance/admin/analytics/by-event" replace />} />
      <Route path="/attendance/admin/leave-requests" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.AttendanceAdminPage /></RouteWrapper>} />
      <Route path="/attendance/admin/qr-code" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.AttendanceAdminPage /></RouteWrapper>} />
      <Route path="/attendance/admin/settings" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.AttendanceAdminPage /></RouteWrapper>} />
      <Route path="/attendance/admin/:tab" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.AttendanceAdminPage /></RouteWrapper>} />
      <Route path="/attendance/admin" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.AttendanceAdminPage /></RouteWrapper>} />
      <Route path="/backfill-event" element={<RouteWrapper requiresAuth><BackfillEventPage /></RouteWrapper>} />

      {/* Public routes */}
      <Route path="/events" element={<RouteWrapper><ROUTE_COMPONENTS.EventsWithSidebarPage /></RouteWrapper>} />
      <Route path="/events-public" element={<RouteWrapper><ROUTE_COMPONENTS.EventsPublicPage /></RouteWrapper>} />
      <Route path="/calendar" element={<RouteWrapper><ROUTE_COMPONENTS.PublicCalendarPage /></RouteWrapper>} />
      <Route path="/create-event" element={<RouteWrapper><ROUTE_COMPONENTS.CreateEventPage /></RouteWrapper>} />
      <Route path="/event/share/:shareSlug" element={<RouteWrapper><ROUTE_COMPONENTS.EventSharePage /></RouteWrapper>} />
      <Route path="/event/:eventId" element={<RouteWrapper><ROUTE_COMPONENTS.EventDetailPage /></RouteWrapper>} />

      {/* Resource routes */}
      <Route path="/sales-resources" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.SalesResourcesPage /></RouteWrapper>} />
      <Route path="/manager-tools" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.ManagerToolsPage /></RouteWrapper>} />
      <Route path="/consultant-tools" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.ConsultantToolsPage /></RouteWrapper>} />
      <Route path="/aia-links" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.AiaLinksPage /></RouteWrapper>} />
      <Route path="/resources-hub" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.ResourcesHubPage /></RouteWrapper>} />

      {/* Coaching and booking routes */}
      <Route path="/coaching/:slug" element={<RouteWrapper><ROUTE_COMPONENTS.CoachingPage /></RouteWrapper>} />
      <Route path="/booking-confirmation/:bookingId" element={<RouteWrapper><ROUTE_COMPONENTS.BookingConfirmation /></RouteWrapper>} />

      {/* Detail pages */}
      <Route path="/activity/:id" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.ActivityDetailPage /></RouteWrapper>} />
      <Route path="/task/:type/:id" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.TaskDetailPage /></RouteWrapper>} />

      {/* Legacy redirects */}
      <Route path="/dashboard" element={<Navigate to="/tracker/log" replace />} />
      <Route path="/dashboard/*" element={<Navigate to="/tracker/log" replace />} />

      {/* Fallback */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}